/**
 * The v1 spreadsheet op vocabulary: the ENTIRE surface the spreadsheet agent
 * may propose. The backend validates a batch with `spreadsheetOpBatchSchema`
 * before any I/O, so this schema is the security and correctness boundary.
 * Every object is strict: an unknown key is refused, never ignored.
 *
 * Not in v1: inserting or deleting rows or columns (decision P2-D1), deleting
 * sheets, pivots, images, merges, data validation, named ranges. Chart types
 * are column, bar and line (P2-D3); pie, scatter and area join by adding them
 * to `CHART_TYPES`.
 *
 * Messages name the field and the fix and never echo a cell value, formula,
 * enum value or unknown key (they may carry PHI into logs); ranges, counts
 * and field names are fine. The raw input is bounded by `preflight.ts` before
 * Zod parses any cell, so an oversized payload costs one short message; when
 * the preflight refuses an op, that op's other errors appear only on the next
 * attempt (the price of the bounded cost).
 */
import { z } from 'zod';
import { CHART_TYPES, H_ALIGNS, MAX_CHART_TITLE_CHARS, MAX_OPS_PER_BATCH, cellValueSchema, colorSchema as color, finiteNumberSchema, formulaSchema, numberFormatSchema, oneOf, strictObject, } from './op-fields';
import { preflightBatch, preflightOp, rangeArea } from './preflight';
import { boundedRange, cellSchema, columnIndex, copyTargetRange, columnLetterSchema, rangeSchema, sheetNameSchema, } from './ranges';
export { CHART_TYPES, COMMON_NUMBER_FORMATS, MAX_CELL_TEXT_CHARS, MAX_CHART_TITLE_CHARS, MAX_FORMULA_CHARS, MAX_NUMBER_FORMAT_CHARS, MAX_OPS_PER_BATCH, MAX_TOUCHED_CELLS_PER_BATCH, } from './op-fields';
const onSheet = { sheet: sheetNameSchema, range: rangeSchema };
const finite = finiteNumberSchema;
const conditionalRuleSchema = z.discriminatedUnion('type', [
    strictObject({ type: z.literal('greaterThan'), value: finite, background: color.optional() }),
    strictObject({ type: z.literal('lessThan'), value: finite, background: color.optional() }),
    strictObject({
        type: z.literal('between'),
        min: finite,
        max: finite,
        background: color.optional(),
    }),
    strictObject({
        type: z.literal('colorScale'),
        minColor: color.optional(),
        maxColor: color.optional(),
    }),
    strictObject({ type: z.literal('dataBar'), color: color.optional() }),
]);
/** Every conditional-format rule type the schema accepts, in schema order. */
export const CONDITIONAL_FORMAT_RULE_TYPES = conditionalRuleSchema.options.map((o) => o.shape.type.value);
const opUnion = z.discriminatedUnion('op', [
    strictObject({
        op: z.literal('setValues'),
        ...onSheet,
        values: z.array(z.array(cellValueSchema)),
    }),
    strictObject({
        op: z.literal('setFormulas'),
        ...onSheet,
        formulas: z.array(z.array(formulaSchema)),
    }),
    // One formula written for the range's top-left cell; code shifts its relative
    // references for every other cell, as a fill handle does.
    strictObject({ op: z.literal('fillFormula'), ...onSheet, formula: formulaSchema }),
    // Stored values and formats copied by code, so the model never retypes them.
    // A formula cell in the source arrives as its result.
    strictObject({
        op: z.literal('copyRange'),
        ...onSheet,
        toSheet: sheetNameSchema,
        to: cellSchema,
    }),
    strictObject({ op: z.literal('setNumberFormat'), ...onSheet, pattern: numberFormatSchema }),
    strictObject({
        op: z.literal('setStyle'),
        ...onSheet,
        bold: z.boolean().optional(),
        italic: z.boolean().optional(),
        fontColor: color.optional(),
        background: color.optional(),
        hAlign: oneOf('hAlign', H_ALIGNS).optional(),
    }),
    strictObject({
        op: z.literal('insertChart'),
        ...onSheet,
        chartType: oneOf('chartType', CHART_TYPES),
        title: z
            .string()
            .max(MAX_CHART_TITLE_CHARS, `title may be at most ${MAX_CHART_TITLE_CHARS} characters`)
            .optional(),
        anchor: cellSchema.optional(),
    }),
    strictObject({
        op: z.literal('sortRange'),
        ...onSheet,
        byColumn: columnLetterSchema,
        ascending: z.boolean(),
        hasHeader: z.boolean(),
    }),
    strictObject({ op: z.literal('setFilter'), ...onSheet }),
    strictObject({ op: z.literal('clearFilter'), sheet: sheetNameSchema }),
    strictObject({ op: z.literal('addConditionalFormat'), ...onSheet, rule: conditionalRuleSchema }),
    strictObject({ op: z.literal('addSheet'), name: sheetNameSchema }),
    strictObject({ op: z.literal('renameSheet'), sheet: sheetNameSchema, name: sheetNameSchema }),
]);
export const SPREADSHEET_OP_NAMES = opUnion.options.map((o) => o.shape.op.value);
function checkSort(op, ctx) {
    const r = boundedRange(op.range);
    if (!r)
        return;
    const at = columnIndex(op.byColumn);
    if (at < r.startColumn || at > r.endColumn) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['byColumn'],
            message: `byColumn ${op.byColumn} is outside range ${op.range}; pick a column inside the range`,
        });
    }
    if (op.hasHeader && r.startRow === r.endRow) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['hasHeader'],
            message: `range ${op.range} has one row, so with hasHeader there is nothing to sort; include the data rows`,
        });
    }
}
function checkCopy(op, ctx) {
    const source = boundedRange(op.range);
    const target = copyTargetRange(op.range, op.to);
    if (!source || !target || op.sheet !== op.toSheet)
        return;
    const apart = target.startRow > source.endRow ||
        source.startRow > target.endRow ||
        target.startColumn > source.endColumn ||
        source.startColumn > target.endColumn;
    if (!apart) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['to'],
            message: `a copy of ${op.range} to ${op.to} overlaps its source; pick a target clear of it`,
        });
    }
}
/** Rules across fields; grid shapes and cells are checked in `preflightOp`. */
function checkOp(op, ctx) {
    const issue = (path, message) => ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });
    switch (op.op) {
        case 'setStyle':
            if ([op.bold, op.italic, op.fontColor, op.background, op.hAlign].every((f) => f === undefined)) {
                issue([], 'setStyle needs at least one of bold, italic, fontColor, background, hAlign');
            }
            return;
        case 'sortRange':
            return checkSort(op, ctx);
        case 'copyRange':
            return checkCopy(op, ctx);
        case 'addConditionalFormat':
            if (op.rule.type === 'between' && op.rule.min > op.rule.max) {
                issue(['rule', 'min'], 'rule.min must be at most rule.max');
            }
            return;
        default:
            return;
    }
}
/**
 * One op, as validated AND as advertised. `z.preprocess` runs the bounded
 * preflight first; its issues are fatal, so the union below is skipped when
 * it refuses (the cost bound), and the JSON Schema the MCP SDK derives is the
 * union itself, so the model sees every op, field and limit. The cost of the
 * bound: when the preflight refuses an op, that op's other errors appear only
 * on the next attempt.
 */
export const spreadsheetOpSchema = z.preprocess((value, ctx) => {
    preflightOp(value, ctx);
    return value;
}, 
// Zod 4 runs a refinement over an op whose fields already failed (Zod 3
// skipped it), and checkOp's messages quote fields such as byColumn.
// Running it only on a fully valid op keeps it from echoing a bad value.
opUnion.superRefine(checkOp, { when: (payload) => payload.issues.length === 0 }));
/**
 * The bare op union, for its shape only (types, docs, JSON Schema). It skips
 * the preflight and the cross-field checks: never parse untrusted input with
 * it; use `spreadsheetOpSchema` or `spreadsheetOpBatchSchema`.
 */
export const spreadsheetOpUnionSchema = opUnion;
/**
 * Cells an op touches, for the batch cap: the range area for every op with a
 * range (setFilter and insertChart included, since the executor reads the
 * range back). clearFilter, addSheet and renameSheet touch no cells: 0.
 */
export function touchedCells(op) {
    return rangeArea(op);
}
/** A batch: count and cell budget checked on the raw input, then each op. */
export const spreadsheetOpBatchSchema = z.preprocess((value, ctx) => {
    preflightBatch(value, ctx);
    return value;
}, z.array(spreadsheetOpSchema).min(1).max(MAX_OPS_PER_BATCH));
//# sourceMappingURL=ops.js.map